import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Checkbox,
  SearchInput,
  Badge,
  ScrollArea,
  toast,
} from '@aaas/shared';
import { Users, Loader2, X } from 'lucide-react';
import { useCVListQuery } from '../hooks/useCVQueries';
import { BULK_CV_LIMIT } from '../types';

const CV_LIMIT = BULK_CV_LIMIT;

interface AddCVsToPoolDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (cvIds: string[]) => Promise<void>;
  workspaceId: string | undefined;
  idToken: string | undefined;
  /** CV IDs already in this pool — shown but pre-excluded */
  existingCvIds?: string[];
}

export function AddCVsToPoolDialog({
  open,
  onClose,
  onConfirm,
  workspaceId,
  idToken,
  existingCvIds = [],
}: AddCVsToPoolDialogProps) {
  const { t } = useTranslation('ats');
  const [selectedCvIds, setSelectedCvIds] = useState<Set<string>>(new Set());
  const [selectedCvNames, setSelectedCvNames] = useState<Map<string, string>>(new Map());
  const [searchQuery, setSearchQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: cvsData, isLoading: cvsLoading } = useCVListQuery({
    workspaceId,
    idToken,
    page: 1,
    size: 50,
    search: searchQuery || undefined,
    enabled: open && !!workspaceId && !!idToken,
    refetchInterval: false,
  });

  const existingSet = useMemo(() => new Set(existingCvIds), [existingCvIds]);
  const allCvs = useMemo(
    () => (cvsData?.data?.items || []).filter((cv) => !existingSet.has(cv.id)),
    [cvsData, existingSet]
  );

  useEffect(() => {
    if (!open) {
      setSelectedCvIds(new Set());
      setSelectedCvNames(new Map());
      setSearchQuery('');
      setError(null);
    }
  }, [open]);

  const handleToggleCV = useCallback((cvId: string, cvName: string) => {
    setSelectedCvIds((prev) => {
      const next = new Set(prev);
      if (next.has(cvId)) {
        next.delete(cvId);
      } else {
        if (next.size >= CV_LIMIT) return prev;
        next.add(cvId);
      }
      return next;
    });
    setSelectedCvNames((prev) => {
      const next = new Map(prev);
      if (next.has(cvId)) {
        next.delete(cvId);
      } else {
        if (next.size >= CV_LIMIT) return prev;
        next.set(cvId, cvName);
      }
      return next;
    });
  }, []);

  const handleToggleAll = useCallback(() => {
    const allVisible = allCvs.length > 0 && allCvs.every((cv) => selectedCvIds.has(cv.id));
    if (allVisible) {
      setSelectedCvIds((prev) => {
        const next = new Set(prev);
        allCvs.forEach((cv) => next.delete(cv.id));
        return next;
      });
      setSelectedCvNames((prev) => {
        const next = new Map(prev);
        allCvs.forEach((cv) => next.delete(cv.id));
        return next;
      });
    } else {
      const unselected = allCvs.filter((cv) => !selectedCvIds.has(cv.id));
      const available = CV_LIMIT - selectedCvIds.size;
      const toAdd = unselected.slice(0, available);
      setSelectedCvIds((prev) => {
        const next = new Set(prev);
        toAdd.forEach((cv) => next.add(cv.id));
        return next;
      });
      setSelectedCvNames((prev) => {
        const next = new Map(prev);
        toAdd.forEach((cv) => {
          if (!next.has(cv.id)) next.set(cv.id, cv.name);
        });
        return next;
      });
    }
  }, [allCvs, selectedCvIds]);

  const handleRemoveSelected = useCallback((cvId: string) => {
    setSelectedCvIds((prev) => {
      const next = new Set(prev);
      next.delete(cvId);
      return next;
    });
    setSelectedCvNames((prev) => {
      const next = new Map(prev);
      next.delete(cvId);
      return next;
    });
  }, []);

  const handleConfirm = useCallback(async () => {
    if (selectedCvIds.size === 0) {
      setError(t('addCvsToPool.selectAtLeastOne'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onConfirm(Array.from(selectedCvIds));
      toast.success(t('addCvsToPool.toast.success', { count: selectedCvIds.size }));
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('addCvsToPool.addFailed');
      setError(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedCvIds, onConfirm, onClose, t]);

  const limitReached = selectedCvIds.size >= CV_LIMIT;
  const allVisibleSelected = allCvs.length > 0 && allCvs.every((cv) => selectedCvIds.has(cv.id));

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-500" />
            {t('addCvsToPool.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Selected CVs summary */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm font-medium text-blue-900 mb-2">
              {t('addCvsToPool.selected', { count: selectedCvIds.size, limit: CV_LIMIT })}
            </p>
            <div className="h-8">
              <ScrollArea className="h-8">
                {selectedCvIds.size > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {Array.from(selectedCvNames.entries()).map(([id, name]) => (
                      <Badge
                        key={id}
                        variant="secondary"
                        className="bg-blue-100 text-blue-900 hover:bg-blue-200 pr-1 py-1"
                      >
                        <span className="mr-1">{name}</span>
                        <button
                          onClick={() => handleRemoveSelected(id)}
                          className="inline-flex items-center justify-center rounded-full hover:bg-blue-300 p-0.5"
                          aria-label={t('addCvsToPool.removeSelectedAria', { name })}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 w-full h-full flex items-center justify-center">
                    {t('addCvsToPool.noCvsSelected')}
                  </p>
                )}
              </ScrollArea>
            </div>
          </div>

          {/* CV selection */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>{t('addCvsToPool.selectCvs')}</Label>
              <div className="flex items-center gap-3">
                {limitReached && (
                  <p className="text-xs text-amber-600">{t('addCvsToPool.limitReached', { limit: CV_LIMIT })}</p>
                )}
                {allCvs.length > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleToggleAll}
                    className="text-xs"
                  >
                    {allVisibleSelected ? t('addCvsToPool.deselectAll') : t('addCvsToPool.selectAll')}
                  </Button>
                )}
              </div>
            </div>

            <SearchInput
              placeholder={t('addCvsToPool.searchPlaceholder')}
              onSearch={setSearchQuery}
              initialValue={searchQuery}
              width="xl"
              className="w-full"
              showClearButton
            />

            {cvsLoading ? (
              <div className="flex items-center justify-center py-8 h-60">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              </div>
            ) : allCvs.length > 0 ? (
              <div className="border border-gray-200 rounded-lg">
                <ScrollArea className="h-60">
                  {allCvs.map((cv) => {
                    const isSelected = selectedCvIds.has(cv.id);
                    const isDisabled = limitReached && !isSelected;
                    return (
                      <label
                        key={cv.id}
                        className={`flex items-start gap-3 p-3 border-b border-gray-100 last:border-b-0 ${
                          isDisabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'
                        }`}
                      >
                        <Checkbox
                          checked={isSelected}
                          disabled={isDisabled}
                          onCheckedChange={() => handleToggleCV(cv.id, cv.name)}
                          className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">{cv.name}</p>
                          {cv.phone && (
                            <p className="text-xs text-gray-500">{cv.phone}</p>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </ScrollArea>
              </div>
            ) : searchQuery ? (
              <p className="text-sm text-gray-500 text-center py-8 h-60">
                {t('addCvsToPool.noCvsMatching', { query: searchQuery })}
              </p>
            ) : (
              <p className="text-sm text-gray-500 text-center py-8 h-60">
                {t('addCvsToPool.noCvsAvailable')}
              </p>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            {t('addCvsToPool.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={selectedCvIds.size === 0 || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('addCvsToPool.adding')}
              </>
            ) : (
              t('addCvsToPool.addCvs', { count: selectedCvIds.size })
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
